/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: TimelinePage.
 * SCOPE: React component or page; sync label column width with sidebar (UC-018); hide completed tasks (UC-019).
 * DEPENDS: M-FE-API, M-FE-AUTH, M-FE-LAYOUT
 * LINKS: M-FE-TIMELINE, UC-018, UC-019
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * TimelinePage - Frontend UI module: TimelinePage.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-28 - UC-019: checkbox «Скрыть завершенные задачи» + filterCompletedTimelineRows.
 * END_CHANGE_SUMMARY
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TimelineIcon from '@mui/icons-material/Timeline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getProjects } from '../../api/projectApi';
import { getFeatures, updateFeature } from '../../api/featureApi';
import { getEmployees, updateEmployee } from '../../api/employeeApi';
import { getProjectRoles } from '../../api/projectRoleApi';
import { getProjectOverheads } from '../../api/projectOverheadApi';
import { getTaskActualHours, upsertTaskActualHours, deleteTaskActualHours } from '../../api/taskActualHoursApi';
import { getWorkingTime } from '../../api/calendarApi';
import Project from '../../models/Project';
import Feature from '../../models/Feature';
import Employee from '../../models/Employee';
import ProjectRole from '../../models/ProjectRole';
import ProjectOverhead from '../../models/ProjectOverhead';
import TaskActualHours from '../../models/TaskActualHours';
import { formatMonthYear, formatDayMonth, startOfDay, toIsoDate } from '../../utils/dateUtils';
import {
  buildDayOffSet,
  buildMonthPages,
  buildTimelineRows,
  buildWorkingHoursByDate,
  filterCompletedTimelineRows,
  filterVisibleDates,
  getPageDates,
  extendDatesToFillColumnCount,
  getTimelineColumnCapacity,
  getTimelineDataRange,
  getTimelineLabelColumnWidth,
  getTimelineViewport,
  getVisiblePageDates,
  TIMELINE_LOOKBACK_WORKING_DAYS,
  TIMELINE_VIEW_MODE,
} from './timelineUtils';
import { actualHoursKey, applyActualsToWorkloadSchedule } from './actualScheduleUtils';
import { useSidebarLayout } from '../../context/SidebarLayoutContext';
import ErrorBoundary from '../../components/ErrorBoundary';
import TimelineGantt from './components/TimelineGantt';
import TimelineLegend from './components/TimelineLegend';
import EditAbsenceDialog from './components/EditAbsenceDialog';
import EditEstimateDialog from './components/EditEstimateDialog';
import EditActualHoursDialog from './components/EditActualHoursDialog';
import EditFeatureDialog from '../features/components/EditFeatureDialog';

function findPageIndexForDate(pages, date) {
  if (!date || !pages.length) {
    return -1;
  }
  return pages.findIndex(
    (page) => page.year === date.getFullYear() && page.month === date.getMonth(),
  );
}

function formatVisibleRange(dates) {
  if (!dates.length) {
    return '';
  }
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (isSameVisibleDay(first, last)) {
    return formatDayMonth(first);
  }
  return `${formatDayMonth(first)} — ${formatDayMonth(last)}`;
}

function isSameVisibleDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

/* START_METHOD_TimelinePage */
/* START_CONTRACT: TimelinePage
 * PURPOSE: Страница таймлайна проекта: диаграмма Ганта фич/сотрудников, навигация по месяцам, редактирование оценок и отсутствий; ширина label от sidebar (UC-018); скрытие завершённых задач (UC-019).
 * INPUTS: {} (projectId из URL params; navCollapsed из SidebarLayoutContext).
 * OUTPUTS: { JSX.Element - страница таймлайна }.
 * SIDE_EFFECTS: HTTP get проектов/фич/сотрудников/ролей/календаря и update фич/сотрудников; навигация; локальный state.
 * LINKS: M-FE-TIMELINE, M-FE-API, M-FE-LAYOUT, UC-018, UC-019
 * END_CONTRACT: TimelinePage
 */
export default function TimelinePage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const numericProjectId = Number(projectId);
  const { navCollapsed } = useSidebarLayout();
  const labelWidth = getTimelineLabelColumnWidth(navCollapsed);

  const [today] = useState(() => startOfDay(new Date()));
  const didAutoPositionRef = useRef(false);

  const [project, setProject] = useState(null);
  const [features, setFeatures] = useState([]);
  const [projectRoles, setProjectRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [overheads, setOverheads] = useState([]);
  const [actualHours, setActualHours] = useState([]);
  const [dayOffSet, setDayOffSet] = useState(() => new Set());
  const [workingHoursByDate, setWorkingHoursByDate] = useState(() => new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [pageIndex, setPageIndex] = useState(0);
  const [hideNonWorkingDays, setHideNonWorkingDays] = useState(false);
  const [hideCompletedTasks, setHideCompletedTasks] = useState(true);
  const [viewMode, setViewMode] = useState(TIMELINE_VIEW_MODE.DETAILED);
  const [scrollToTodayKey, setScrollToTodayKey] = useState(0);
  const ganttContainerRef = useRef(null);
  const [columnCapacity, setColumnCapacity] = useState(0);

  const [editingAbsence, setEditingAbsence] = useState(null);
  const [editingEstimate, setEditingEstimate] = useState(null);
  const [editingActualHours, setEditingActualHours] = useState(null);
  const [editFeatureOpen, setEditFeatureOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);

  /* START_METHOD_fetchData */
  /* START_CONTRACT: fetchData
   * PURPOSE: Загрузить проект, фичи, сотрудников, роли и рабочее время; заполнить state таймлайна.
   * INPUTS: { signal: AbortSignal }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP getProjects/getFeatures/getEmployees/getProjectRoles/getWorkingTime; множество setState.
   * LINKS: M-FE-API, M-FE-TIMELINE
   * END_CONTRACT: fetchData
   */
  const fetchData = useCallback(async (signal) => {
    if (!numericProjectId) {
      /* START_BLOCK_INVALID_ID */
      setError('Некорректный идентификатор проекта');
      setLoading(false);
      return;
      /* END_BLOCK_INVALID_ID */
    }

    try {
      const [projectsRes, featuresRes, employeesRes, rolesRes, overheadsRes, actualHoursRes] = await Promise.all([
        getProjects(signal),
        getFeatures(numericProjectId, signal),
        getEmployees(numericProjectId, signal),
        getProjectRoles(numericProjectId, signal),
        getProjectOverheads(numericProjectId, signal),
        getTaskActualHours(numericProjectId, signal),
      ]);

      const foundProject = projectsRes.data
        .map(Project.fromResponse)
        .find((p) => p.id === numericProjectId);

      if (!foundProject) {
        /* START_BLOCK_PROJECT_NOT_FOUND */
        setError('Проект не найден');
        return;
        /* END_BLOCK_PROJECT_NOT_FOUND */
      }

      /* START_BLOCK_LOAD_OK */
      const featureModels = featuresRes.data.map(Feature.fromResponse);
      setProject(foundProject);
      setFeatures(featureModels);
      setProjectRoles(rolesRes.data.map(ProjectRole.fromResponse));
      setEmployees(employeesRes.data.map(Employee.fromResponse));
      setOverheads(overheadsRes.data.map(ProjectOverhead.fromResponse));
      setActualHours(actualHoursRes.data.map(TaskActualHours.fromResponse));
      setError('');
      /* END_BLOCK_LOAD_OK */

      /* START_BLOCK_RESOLVE_RANGE */
      const initialDataRange = getTimelineDataRange(today);
      /* END_BLOCK_RESOLVE_RANGE */

      try {
        const calendarRes = await getWorkingTime(
          toIsoDate(initialDataRange.start),
          toIsoDate(initialDataRange.end),
          signal,
        );
        /* START_BLOCK_CALENDAR_OK */
        const nextDayOffSet = buildDayOffSet(calendarRes.data, initialDataRange.start);
        const nextWorkingHoursByDate = buildWorkingHoursByDate(
          calendarRes.data,
          initialDataRange.start,
        );
        setDayOffSet(nextDayOffSet);
        setWorkingHoursByDate(nextWorkingHoursByDate);
        const refinedDataRange = getTimelineDataRange(
          today,
          TIMELINE_LOOKBACK_WORKING_DAYS,
          nextDayOffSet,
          nextWorkingHoursByDate,
        );
        if (refinedDataRange.start < initialDataRange.start) {
          const extendedCalendarRes = await getWorkingTime(
            toIsoDate(refinedDataRange.start),
            toIsoDate(refinedDataRange.end),
            signal,
          );
          const extendedDayOffSet = buildDayOffSet(extendedCalendarRes.data, refinedDataRange.start);
          const extendedWorkingHoursByDate = buildWorkingHoursByDate(
            extendedCalendarRes.data,
            refinedDataRange.start,
          );
          setDayOffSet(extendedDayOffSet);
          setWorkingHoursByDate(extendedWorkingHoursByDate);
        }
        /* END_BLOCK_CALENDAR_OK */
      } catch (calendarErr) {
        /* START_BLOCK_CALENDAR_FAIL */
        if (signal?.aborted) return;
        console.warn('[TimelinePage][fetchData][CALENDAR_FAIL] working time load failed, fallback to empty', {
          status: calendarErr.response?.status,
        });
        setDayOffSet(new Set());
        setWorkingHoursByDate(new Map());
        /* END_BLOCK_CALENDAR_FAIL */
      }
    } catch (err) {
      /* START_BLOCK_LOAD_FAIL */
      if (signal?.aborted) return;
      console.warn('[TimelinePage][fetchData][LOAD_FAIL] timeline load failed', {
        status: err.response?.status,
      });
      setError(err.response?.data?.message || 'Ошибка загрузки таймлайна');
      /* END_BLOCK_LOAD_FAIL */
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [numericProjectId, today]);
  /* END_METHOD_fetchData */

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  /* START_METHOD_handleRetry */
  /* START_CONTRACT: handleRetry
   * PURPOSE: Повторить загрузку данных таймлайна после ошибки.
   * INPUTS: {}.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: setLoading(true); setError(''); fetchData().
   * LINKS: M-FE-TIMELINE
   * END_CONTRACT: handleRetry
   */
  const handleRetry = useCallback(() => {
    setLoading(true);
    setError('');
    fetchData();
  }, [fetchData]);
  /* END_METHOD_handleRetry */

  const viewport = useMemo(
    () => getTimelineViewport(
      today,
      TIMELINE_LOOKBACK_WORKING_DAYS,
      dayOffSet,
      workingHoursByDate,
    ),
    [today, dayOffSet, workingHoursByDate],
  );
  const dataRange = useMemo(
    () => getTimelineDataRange(today, TIMELINE_LOOKBACK_WORKING_DAYS, dayOffSet, workingHoursByDate),
    [today, dayOffSet, workingHoursByDate],
  );

  const pages = useMemo(() => buildMonthPages(dataRange.start, dataRange.end), [dataRange]);
  const currentPage = pages[pageIndex];
  const currentDates = useMemo(
    () => getVisiblePageDates(currentPage, viewport, dataRange, today),
    [currentPage, viewport, dataRange, today],
  );
  const visibleDates = useMemo(
    () => filterVisibleDates(currentDates, dayOffSet, workingHoursByDate, hideNonWorkingDays),
    [currentDates, dayOffSet, workingHoursByDate, hideNonWorkingDays],
  );

  const displayDates = useMemo(
    () => extendDatesToFillColumnCount(
      visibleDates,
      columnCapacity,
      dataRange.end,
      dayOffSet,
      workingHoursByDate,
      hideNonWorkingDays,
    ),
    [
      visibleDates,
      columnCapacity,
      dataRange.end,
      dayOffSet,
      workingHoursByDate,
      hideNonWorkingDays,
    ],
  );
  const workloadSchedule = useMemo(
    () => applyActualsToWorkloadSchedule(
      features,
      employees,
      projectRoles,
      workingHoursByDate,
      overheads,
      actualHours,
    ),
    [features, employees, projectRoles, workingHoursByDate, overheads, actualHours],
  );
  const actualHoursByKey = useMemo(() => {
    const map = new Map();
    actualHours.forEach((item) => {
      map.set(actualHoursKey(item.featureId, item.projectRoleId, item.workDate), item);
    });
    return map;
  }, [actualHours]);
  const allRows = useMemo(
    () => buildTimelineRows(
      features,
      employees,
      projectRoles,
      workingHoursByDate,
      overheads,
      actualHours,
      workloadSchedule,
    ),
    [features, employees, projectRoles, workingHoursByDate, overheads, actualHours, workloadSchedule],
  );
  const rows = useMemo(
    () => filterCompletedTimelineRows(allRows, hideCompletedTasks, toIsoDate(today)),
    [allRows, hideCompletedTasks, today],
  );

  useEffect(() => {
    const container = ganttContainerRef.current;
    if (!container) {
      return undefined;
    }
    const updateCapacity = () => {
      setColumnCapacity(getTimelineColumnCapacity(container.clientWidth, labelWidth));
    };
    updateCapacity();
    const observer = new ResizeObserver(updateCapacity);
    observer.observe(container);
    return () => observer.disconnect();
  }, [rows.length, loading, pageIndex, hideNonWorkingDays, labelWidth]);

  /* START_METHOD_syncLabelWidth */
  /* START_CONTRACT: syncLabelWidth
   * PURPOSE: Залогировать ветку ширины label-столбца при смене navCollapsed (UC-018).
   * INPUTS: { navCollapsed, labelWidth from closure }.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: console.info branch markers.
   * LINKS: M-FE-TIMELINE, UC-018
   * END_CONTRACT: syncLabelWidth
   */
  useEffect(() => {
    /* START_BLOCK_LABEL_WIDTH_SYNC */
    if (navCollapsed) {
      console.info('[TimelinePage][syncLabelWidth][BLOCK_LABEL_WIDTH_EXPANDED] label column 2x', {
        labelWidth,
        branch: 'expanded',
      });
    } else {
      console.info('[TimelinePage][syncLabelWidth][BLOCK_LABEL_WIDTH_COLLAPSED] label column base', {
        labelWidth,
        branch: 'collapsed',
      });
    }
    /* END_BLOCK_LABEL_WIDTH_SYNC */
  }, [navCollapsed, labelWidth]);
  /* END_METHOD_syncLabelWidth */

  const goToToday = useCallback(() => {
    const targetIndex = findPageIndexForDate(pages, today);
    if (targetIndex >= 0) {
      setPageIndex(targetIndex);
    }
    setScrollToTodayKey((prev) => prev + 1);
  }, [pages, today]);

  useEffect(() => {
    /* START_BLOCK_AUTO_POSITION_PAGE */
    if (didAutoPositionRef.current || loading || allRows.length === 0) {
      return;
    }
    const targetIndex = findPageIndexForDate(pages, today);
    didAutoPositionRef.current = true;
    if (targetIndex >= 0) {
      setPageIndex(targetIndex);
    }
    /* END_BLOCK_AUTO_POSITION_PAGE */
  }, [loading, allRows, pages, today]);

  const editingEmployee = editingAbsence
    ? employees.find((e) => e.id === editingAbsence.employeeId) ?? null
    : null;
  const otherAbsences = useMemo(() => {
    if (!editingEmployee || !editingAbsence) {
      return [];
    }
    return editingEmployee.absences
      .filter((a) => a.id !== editingAbsence.absence.id)
      .map((a) => ({ from: a.from, to: a.to }));
  }, [editingEmployee, editingAbsence]);

  /* START_METHOD_handleAbsenceClick */
  /* START_CONTRACT: handleAbsenceClick
   * PURPOSE: Открыть диалог редактирования выбранного отсутствия сотрудника.
   * INPUTS: { row: TimelineRow, absence: Absence }.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: setEditingAbsence.
   * LINKS: M-FE-TIMELINE
   * END_CONTRACT: handleAbsenceClick
   */
  const handleAbsenceClick = useCallback((row, absence) => {
    setEditingAbsence({ employeeId: row.employeeId, absence });
  }, []);
  /* END_METHOD_handleAbsenceClick */

  /* START_METHOD_handleEstimateClick */
  /* START_CONTRACT: handleEstimateClick
   * PURPOSE: Открыть диалог редактирования оценки роли внутри фичи.
   * INPUTS: { group: FeatureGroup, row: RoleRow }.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: setEditingEstimate.
   * LINKS: M-FE-TIMELINE
   * END_CONTRACT: handleEstimateClick
   */
  const handleEstimateClick = useCallback((group, row) => {
    setEditingEstimate({
      featureId: group.featureId,
      featureName: group.featureName,
      projectRoleId: row.projectRoleId,
      roleName: row.name,
      currentEstimate: row.hours,
    });
  }, []);
  /* END_METHOD_handleEstimateClick */

  /* START_METHOD_handleFeatureClick */
  /* START_CONTRACT: handleFeatureClick
   * PURPOSE: Найти фичу по id и открыть диалог её редактирования.
   * INPUTS: { featureId: number }.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: setEditingFeature; setEditFeatureOpen.
   * LINKS: M-FE-TIMELINE, M-FE-FEATURES
   * END_CONTRACT: handleFeatureClick
   */
  const handleFeatureClick = useCallback((featureId) => {
    const feature = features.find((f) => f.id === featureId);
    if (!feature) return;
    setEditingFeature(feature);
    setEditFeatureOpen(true);
  }, [features]);
  /* END_METHOD_handleFeatureClick */

  /* START_METHOD_handleUpdateFeature */
  /* START_CONTRACT: handleUpdateFeature
   * PURPOSE: Обновить фичу и перезагрузить данные таймлайна.
   * INPUTS: { id: number, payload: object }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP updateFeature; fetchData.
   * LINKS: M-FE-API
   * END_CONTRACT: handleUpdateFeature
   */
  const handleUpdateFeature = useCallback(async (id, payload) => {
    await updateFeature(numericProjectId, id, payload);
    await fetchData();
  }, [numericProjectId, fetchData]);
  /* END_METHOD_handleUpdateFeature */

  /* START_METHOD_handleSaveEstimate */
  /* START_CONTRACT: handleSaveEstimate
   * PURPOSE: Сохранить новую оценку роли в фиче и перезагрузить таймлайн.
   * INPUTS: { newEstimate: number }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP updateFeature; fetchData.
   * LINKS: M-FE-API
   * END_CONTRACT: handleSaveEstimate
   */
  const handleSaveEstimate = useCallback(async (newEstimate) => {
    if (!editingEstimate) return;
    const feature = features.find((f) => f.id === editingEstimate.featureId);
    if (!feature) return;
    const estimates = feature.estimates.map((e) => ({
      projectRoleId: e.projectRoleId,
      estimate: e.projectRoleId === editingEstimate.projectRoleId ? newEstimate : e.estimate,
    }));
    await updateFeature(numericProjectId, feature.id, {
      name: feature.name,
      startDate: feature.startDate ?? null,
      estimates,
    });
    await fetchData();
  }, [editingEstimate, features, numericProjectId, fetchData]);
  /* END_METHOD_handleSaveEstimate */

  /* START_METHOD_saveEmployeeAbsences */
  /* START_CONTRACT: saveEmployeeAbsences
   * PURPOSE: Сохранить набор отсутствий сотрудника и перезагрузить таймлайн.
   * INPUTS: { employee: Employee, absences: Absence[] }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP updateEmployee; fetchData.
   * LINKS: M-FE-API
   * END_CONTRACT: saveEmployeeAbsences
   */
  const saveEmployeeAbsences = useCallback(async (employee, absences) => {
    await updateEmployee(numericProjectId, employee.id, {
      name: employee.name,
      projectRoleId: employee.projectRoleId,
      absences,
    });
    await fetchData();
  }, [numericProjectId, fetchData]);
  /* END_METHOD_saveEmployeeAbsences */

  /* START_METHOD_handleSaveAbsence */
  /* START_CONTRACT: handleSaveAbsence
   * PURPOSE: Заменить редактируемое отсутствие новыми датами и сохранить.
   * INPUTS: { from: string, to: string }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: делегирует saveEmployeeAbsences (HTTP).
   * LINKS: M-FE-TIMELINE
   * END_CONTRACT: handleSaveAbsence
   */
  const handleSaveAbsence = useCallback(async ({ from, to }) => {
    if (!editingEmployee || !editingAbsence) return;
    const absences = editingEmployee.absences.map((a) => (
      a.id === editingAbsence.absence.id
        ? { from, to }
        : { from: a.from, to: a.to }
    ));
    await saveEmployeeAbsences(editingEmployee, absences);
  }, [editingEmployee, editingAbsence, saveEmployeeAbsences]);
  /* END_METHOD_handleSaveAbsence */

  /* START_METHOD_handleDeleteAbsence */
  /* START_CONTRACT: handleDeleteAbsence
   * PURPOSE: Удалить редактируемое отсутствие из набора сотрудника и сохранить.
   * INPUTS: {}.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: делегирует saveEmployeeAbsences (HTTP).
   * LINKS: M-FE-TIMELINE
   * END_CONTRACT: handleDeleteAbsence
   */
  const handleDeleteAbsence = useCallback(async () => {
    if (!editingEmployee || !editingAbsence) return;
    const absences = editingEmployee.absences
      .filter((a) => a.id !== editingAbsence.absence.id)
      .map((a) => ({ from: a.from, to: a.to }));
    await saveEmployeeAbsences(editingEmployee, absences);
  }, [editingEmployee, editingAbsence, saveEmployeeAbsences]);
  /* END_METHOD_handleDeleteAbsence */

  /* START_METHOD_handleActualHoursClick */
  const handleActualHoursClick = useCallback((group, row, workDate, plannedHours, actualRecord) => {
    setEditingActualHours({
      featureId: group.featureId,
      featureName: group.featureName,
      projectRoleId: row.projectRoleId,
      employeeName: row.name,
      workDate,
      plannedHours: plannedHours ?? 0,
      currentActual: actualRecord?.actualHours ?? null,
      recordId: actualRecord?.id ?? null,
    });
  }, []);
  /* END_METHOD_handleActualHoursClick */

  /* START_METHOD_handleSaveActualHours */
  const handleSaveActualHours = useCallback(async (actualHoursValue) => {
    if (!editingActualHours) return;
    await upsertTaskActualHours(numericProjectId, {
      featureId: editingActualHours.featureId,
      projectRoleId: editingActualHours.projectRoleId,
      workDate: editingActualHours.workDate,
      actualHours: actualHoursValue,
      plannedHours: editingActualHours.plannedHours,
    });
    await fetchData();
  }, [editingActualHours, numericProjectId, fetchData]);
  /* END_METHOD_handleSaveActualHours */

  /* START_METHOD_handleDeleteActualHours */
  const handleDeleteActualHours = useCallback(async () => {
    if (!editingActualHours?.recordId) return;
    await deleteTaskActualHours(numericProjectId, editingActualHours.recordId);
    await fetchData();
  }, [editingActualHours, numericProjectId, fetchData]);
  /* END_METHOD_handleDeleteActualHours */

  const monthLabel = currentPage
    ? formatMonthYear(new Date(currentPage.year, currentPage.month, 1))
    : '';
  const visibleRangeLabel = formatVisibleRange(displayDates);
  const isCurrentMonthPage = findPageIndexForDate(pages, today) === pageIndex;

  const canGoPrev = pageIndex > 0;
  const canGoNext = pageIndex < pages.length - 1;

  /* START_METHOD_handleViewModeChange */
  const handleViewModeChange = useCallback((_event, nextMode) => {
    if (!nextMode) {
      return;
    }
    /* START_BLOCK_VIEW_MODE_SWITCH */
    if (typeof console !== 'undefined') {
      console.debug('[TimelinePage][handleViewModeChange][BLOCK_VIEW_MODE_SWITCH]', {
        viewMode: nextMode,
      });
    }
    setViewMode(nextMode);
    /* END_BLOCK_VIEW_MODE_SWITCH */
  }, []);
  /* END_METHOD_handleViewModeChange */

  /* START_METHOD_handleHideCompletedChange */
  /* START_CONTRACT: handleHideCompletedChange
   * PURPOSE: Переключает скрытие завершённых задач на таймлайне (UC-019).
   * INPUTS: { event: ChangeEvent }.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: setHideCompletedTasks; console.debug branch markers.
   * LINKS: V-M-FE-TIMELINE, UC-019
   * END_CONTRACT: handleHideCompletedChange
   */
  const handleHideCompletedChange = useCallback((event) => {
    const checked = event.target.checked;
    if (checked) {
      /* START_BLOCK_HIDE_COMPLETED_ON */
      if (typeof console !== 'undefined') {
        console.debug('[TimelinePage][handleHideCompletedChange][BLOCK_HIDE_COMPLETED_ON]');
      }
      setHideCompletedTasks(true);
      /* END_BLOCK_HIDE_COMPLETED_ON */
    } else {
      /* START_BLOCK_HIDE_COMPLETED_OFF */
      if (typeof console !== 'undefined') {
        console.debug('[TimelinePage][handleHideCompletedChange][BLOCK_HIDE_COMPLETED_OFF]');
      }
      setHideCompletedTasks(false);
      /* END_BLOCK_HIDE_COMPLETED_OFF */
    }
  }, []);
  /* END_METHOD_handleHideCompletedChange */

  const timelineToggleSx = {
    '& .MuiToggleButtonGroup-grouped': {
      border: 1,
      borderColor: 'divider',
      borderRadius: '4px !important',
      mx: 0,
      px: 1.25,
      py: 0.35,
      textTransform: 'none',
      fontSize: '0.75rem',
      '&.Mui-selected': {
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
        color: 'primary.main',
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.35),
        fontWeight: 600,
        '&:hover': {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.18),
        },
      },
    },
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !project) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          size="small"
          onClick={() => navigate('/projects')}
          sx={{ mb: 2 }}
        >
          К проектам
        </Button>
        <Alert
          severity="error"
          action={(
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRetry}
            >
              Повторить
            </Button>
          )}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          size="small"
          onClick={() => navigate('/projects')}
          sx={{ flexShrink: 0 }}
        >
          К проектам
        </Button>
        {allRows.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 2,
              flexWrap: 'wrap',
              minWidth: 0,
            }}
          >
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              aria-label="Режим отображения таймлайна"
              sx={{ ...timelineToggleSx, flexShrink: 0 }}
            >
              <ToggleButton value={TIMELINE_VIEW_MODE.DETAILED} aria-label="Детализация">
                Детализация
              </ToggleButton>
              <ToggleButton value={TIMELINE_VIEW_MODE.OVERVIEW} aria-label="Сводка">
                Сводка
              </ToggleButton>
            </ToggleButtonGroup>
            <TimelineLegend viewMode={viewMode} />
          </Box>
        ) : null}
      </Box>

      <Typography variant="h5" sx={{ mb: 2 }}>
        Таймлайн — {project?.name}
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRetry}
            >
              Повторить
            </Button>
          )}
        >
          {error}
        </Alert>
      )}

      {allRows.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            py: 8,
            px: 3,
            textAlign: 'center',
          }}
        >
          <TimelineIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography color="text.secondary">
            Нет активных фич с оценками для отображения на таймлайне
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate(`/projects/${numericProjectId}/features`)}
            >
              Перейти к задачам
            </Button>
            <Button
              size="small"
              onClick={() => navigate(`/projects/${numericProjectId}/employees`)}
            >
              Сотрудники
            </Button>
          </Box>
        </Paper>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              mb: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => setPageIndex((prev) => Math.max(0, prev - 1))}
                disabled={!canGoPrev}
                aria-label="Предыдущий месяц"
              >
                <ChevronLeftIcon />
              </IconButton>
              <Box sx={{ minWidth: 160, textAlign: 'center' }}>
                <Typography
                  variant="subtitle1"
                  sx={{ textTransform: 'capitalize', lineHeight: 1.3 }}
                >
                  {monthLabel}
                </Typography>
                {visibleRangeLabel ? (
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                    {visibleRangeLabel}
                    {hideNonWorkingDays ? ' · без выходных' : ''}
                  </Typography>
                ) : null}
              </Box>
              <IconButton
                size="small"
                onClick={() => setPageIndex((prev) => Math.min(pages.length - 1, prev + 1))}
                disabled={!canGoNext}
                aria-label="Следующий месяц"
              >
                <ChevronRightIcon />
              </IconButton>
              <Button
                size="small"
                variant={isCurrentMonthPage ? 'text' : 'outlined'}
                onClick={goToToday}
                sx={{ ml: 0.5, minWidth: 72 }}
              >
                Сегодня
              </Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small"
                    checked={hideNonWorkingDays}
                    onChange={(event) => setHideNonWorkingDays(event.target.checked)}
                  />
                )}
                label={(
                  <Typography variant="caption" color="text.secondary">
                    Скрыть нерабочие дни
                  </Typography>
                )}
                sx={{ mr: 0, flexShrink: 0 }}
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small"
                    checked={hideCompletedTasks}
                    onChange={handleHideCompletedChange}
                  />
                )}
                label={(
                  <Typography variant="caption" color="text.secondary">
                    Скрыть завершенные задачи
                  </Typography>
                )}
                sx={{ mr: 0, flexShrink: 0 }}
              />
            </Box>
          </Box>

          {rows.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                px: 3,
                textAlign: 'center',
              }}
            >
              <Typography color="text.secondary">
                Все завершённые задачи скрыты. Снимите флажок «Скрыть завершенные задачи», чтобы показать их.
              </Typography>
            </Paper>
          ) : (
            <ErrorBoundary
              fallbackMessage="Не удалось отобразить диаграмму. Попробуйте обновить данные."
              onReset={handleRetry}
            >
              <Box ref={ganttContainerRef} sx={{ width: '100%' }}>
                <TimelineGantt
                  dates={displayDates}
                  dayOffSet={dayOffSet}
                  workingHoursByDate={workingHoursByDate}
                  rows={rows}
                  today={today}
                  viewMode={viewMode}
                  labelWidth={labelWidth}
                  onAbsenceClick={handleAbsenceClick}
                  onEstimateClick={handleEstimateClick}
                  onFeatureClick={handleFeatureClick}
                  onActualHoursClick={handleActualHoursClick}
                  actualHoursByKey={actualHoursByKey}
                  scrollToTodayKey={scrollToTodayKey}
                />
              </Box>
            </ErrorBoundary>
          )}
        </>
      )}

      <EditAbsenceDialog
        open={Boolean(editingAbsence)}
        onClose={() => setEditingAbsence(null)}
        employeeName={editingEmployee?.name ?? ''}
        absence={editingAbsence?.absence ?? null}
        otherAbsences={otherAbsences}
        onSave={handleSaveAbsence}
        onDelete={handleDeleteAbsence}
      />

      <EditEstimateDialog
        open={Boolean(editingEstimate)}
        onClose={() => setEditingEstimate(null)}
        featureName={editingEstimate?.featureName ?? ''}
        roleName={editingEstimate?.roleName ?? ''}
        currentEstimate={editingEstimate?.currentEstimate ?? null}
        onSave={handleSaveEstimate}
      />

      <EditActualHoursDialog
        open={Boolean(editingActualHours)}
        onClose={() => setEditingActualHours(null)}
        context={editingActualHours}
        onSave={handleSaveActualHours}
        onDelete={handleDeleteActualHours}
      />

      <EditFeatureDialog
        open={editFeatureOpen}
        onClose={() => {
          setEditFeatureOpen(false);
          setEditingFeature(null);
        }}
        onUpdate={handleUpdateFeature}
        feature={editingFeature}
        projectRoles={projectRoles}
      />
    </Box>
  );
}
/* END_METHOD_TimelinePage */
